import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { http } from "../../lib/http";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

const ROLES = ["admin", "receptionist", "teacher", "parent", "student"];
const ACTIONS = ["view", "create", "edit", "delete"];

export default function RolesAdmin() {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [role, setRole] = useState("receptionist");
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    http.get("/admin/roles").then((r) => setModules(r.data.modules));
  }, []);

  useEffect(() => {
    http.get(`/admin/roles/${role}`).then((r) => { setPerms(r.data.permissions); setDirty(false); });
  }, [role]);

  const toggle = (m, a) => {
    setPerms((p) => {
      const next = { ...p, [m]: { ...(p[m] || {}), [a]: !(p[m]?.[a]) } };
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/roles/${role}/permissions`, { permissions: perms });
      toast.success(t("roles.saved"));
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const locked = role === "admin";

  return (
    <div data-testid="roles-admin-page">
      <PageHeader
        title={t("roles.title")}
        subtitle={t("roles.subtitle")}
        action={
          <Button onClick={save} disabled={!dirty || busy || locked} className="bg-[#C8102E] hover:bg-[#a50d25]" data-testid="save-perms-button">
            {busy ? t("common.loading") : t("roles.saveAll")}
          </Button>
        }
      />
      <PageBody>
        <div className="mb-4 flex items-center gap-3">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-64 bg-white" data-testid="role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`users.roles.${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          {locked && <span className="text-xs text-slate-500">{t("roles.adminLocked")}</span>}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="perm-matrix">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-medium">{t("roles.module")}</th>
                {ACTIONS.map((a) => (
                  <th key={a} className="px-5 py-3 text-center text-xs uppercase tracking-wider text-slate-500 font-medium">{a}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {modules.map((m) => (
                <tr key={m} className="hover:bg-slate-50/50" data-testid={`perm-row-${m}`}>
                  <td className="px-5 py-3 text-[#0A1F44]">{t(`roles.modules.${m}`, m)}</td>
                  {ACTIONS.map((a) => (
                    <td key={a} className="px-5 py-3 text-center">
                      <Checkbox
                        checked={!!perms[m]?.[a]}
                        disabled={locked}
                        onCheckedChange={() => toggle(m, a)}
                        data-testid={`perm-${m}-${a}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>
    </div>
  );
}
